// @ts-check
import { useCallback } from "react";
import { useAxios } from "../../../common_nextgen/axios/AxiosHelper";
import { URL_SCREEN_NO } from "../../../common_nextgen/utils/Constants";
import { LegacyResolvers } from "../../../common_nextgen/utils/UtilFunctions";
import { useModal } from "../../../common_nextgen/hooks/ModalContext";
import { navigateToPresto } from "../../../common_nextgen/utils/UtilFunctions";
import { useToast } from "../../../common_nextgen/hooks/ToastContext";


export const useLegacyLookup = () => {
  const { axios_helper_no_header } = useAxios();
  const { showModal } = useModal();
  const toast = useToast();

  const fetchParamData = useCallback(async (formData, apiFieldInputs, apiFieldMapping, screenNumber) => {
    const queryParams = apiFieldInputs.reduce((acc, item) => {
      acc[item.target] = formData[item.source];
      return acc;
    }, {});
    const raw_response = await axios_helper_no_header(
      "GET",
      URL_SCREEN_NO[screenNumber],
      null,
      queryParams
    );

    const response = raw_response.responseData;
    if (response && Object.keys(response).length) {
      const mappedData = apiFieldMapping.reduce((mappedObj, mapping) => {
        mappedObj[mapping.legacy] = response[mapping.source];
        return mappedObj;
      }, {});
      return mappedData;
    }
    return null;
  }, [axios_helper_no_header]);

  /**
   * @typedef { {
   *  viewType: "legacy" | string,
   *  fieldMapping: {
   *    default: {
   *      source: string,
   *      target: string,
   *      legacy: string,
   *      paramDataKey: string,
   *      resolver?: (formItem: string | number | boolean) => string | number | boolean,
   *    }[]
   *  },
   * apiFetch?: false,
   * } } BaseParams
   */
  
  /**
   * @typedef { {
   *  apiFetch: true,
   *  apiFieldMapping?: {source: string, legacy: string}[],
   *  apiFieldInputs?: {source: string, target: string}[],
   * } } ApiFetchExtraParams
   */

  /**
   * @typedef { BaseParams & ApiFetchExtraParams } ApiFetchParams
   * 
   * @typedef { ApiFetchParams | BaseParams } Params
   * 
   * @type { (lookupScreen: {default: string}, params: Params, formData: Record<string, any>) => Promise<void> }
   */
  const viewLookupDetails = useCallback(async (lookupScreen, params, formData) => {
    const { viewType, fieldMapping, apiFetch } = params;
    const { default: screenNumber } = lookupScreen;

    if (viewType === "legacy") {
      let apiParamData = null;
      if (apiFetch) {
        try {
          const { apiFieldMapping, apiFieldInputs } = params;
          apiParamData = await fetchParamData(formData, apiFieldInputs, apiFieldMapping, screenNumber);
          const legacyMessageParameters = apiFieldInputs.reduce((acc, field) => {
            acc[field.target] = formData[field.source] || apiParamData[field.source];
            return acc;
          }, apiParamData? apiParamData : {});

          navigateToPresto(screenNumber,legacyMessageParameters);
          return;
        } catch (error) {
          console.error("%cError fetching param data:", 'background: red; color: white', error);
          toast.current?.show({
            severity: "error",
            summary: "",
            detail: error.message ?? "Error in showing details",
            life: 3000,
          });
        }
      }

      // if api fetch is not needed and all data is present
      const { default: viewParams } = fieldMapping;
      const paramData = viewParams.reduce((acc, item) => {
        const resolver = item.resolver ? LegacyResolvers[item.resolver] : null;
        acc[item.legacy] = resolver ? resolver(formData[item.source]) : formData[item.source];
        return acc;
      }, {});
      navigateToPresto(screenNumber, paramData);
    } else {
      showModal(lookupScreen, { params }, null, formData, true);
    }
  }, [fetchParamData, showModal]);

  return { viewLookupDetails };
};
